import { format, parseISO } from "date-fns"
import type { DeliveryLine, DeliveryOrder } from "@/api/inbound_po_line.tsx"

/** Where H&B takes a receiving dispute: Accounts Payable raises (and cancels) shortage debits. */
export const HB_AP_EMAIL = "AccountsPayable_Depot@hollandandbarrett.com"

export type DeliveryFilter = "attention" | "late" | "short" | "open" | "all"

/** A PO needs attention when it arrived late, arrived short, or was paid for less than ordered. */
export function needs_attention(order: DeliveryOrder): boolean {
  return !order.is_open && (order.days_late !== null || order.short_units > 0 || order.invoice?.paid_for_received_only === true)
}

export function filter_orders(orders: DeliveryOrder[], filter: DeliveryFilter): DeliveryOrder[] {
  switch (filter) {
    case "attention":
      return orders.filter(needs_attention)
    case "late":
      return orders.filter((o) => o.days_late !== null)
    case "short":
      return orders.filter((o) => o.short_units > 0)
    case "open":
      return orders.filter((o) => o.is_open)
    case "all":
      return orders
  }
}

export type StatusTone = "open" | "ok" | "late" | "short" | "late_short"

/** What happened to a PO, in a few words, and how loudly to say it. */
export function delivery_status(order: DeliveryOrder): { label: string; tone: StatusTone } {
  if (order.is_open) {
    return { label: "Being received", tone: "open" }
  }
  const late = order.days_late !== null ? `${order.days_late} day${order.days_late === 1 ? "" : "s"} late` : null
  const short = order.short_units > 0 ? `${format_units(order.short_units)} short` : null
  if (late && short) {
    return { label: `${late}, ${short}`, tone: "late_short" }
  }
  if (late) {
    return { label: late, tone: "late" }
  }
  if (short) {
    return { label: short, tone: "short" }
  }
  return { label: "On time, in full", tone: "ok" }
}

/** `5616` → `5,616`; nothing → an em dash. */
export function format_units(units: number | null | undefined): string {
  if (units === null || units === undefined) {
    return "—"
  }
  return units.toLocaleString("en-GB", { maximumFractionDigits: 2 })
}

export function format_day(date: string | null | undefined): string {
  return date ? format(parseISO(date), "d MMM yyyy") : "—"
}

/**
 * A pre-filled email to H&B Accounts Payable for a line the brand delivered in full but H&B's
 * Vendor Portal counts short, so the receipt is corrected before a shortage debit or a short payment
 * stands.
 */
export function shortage_dispute_mailto(order: DeliveryOrder, line: DeliveryLine, signer: string, supplier_number: string | null): string {
  const subject = `Receiving discrepancy - PO ${order.po_number} - SKU ${line.item_number}${supplier_number ? ` - ${supplier_number}` : ""}`
  const received = line.received_date ? ` on ${format_day(line.received_date)}` : ""
  const body = [
    `Hello,`,
    ``,
    `The Vendor Portal shows PO ${order.po_number}, SKU ${line.item_number}${line.description ? ` (${line.description})` : ""}, received${received} as ${format_units(line.quantity_received)} of ${format_units(line.quantity_ordered)} units.`,
    `We delivered this line in full; our proof of delivery is attached. Please correct the receipt, and cancel any shortage debit or short payment raised against it.`,
    ``,
    ...(supplier_number ? [`Supplier number: ${supplier_number}`, ``] : []),
    `Thank you,`,
    signer,
  ].join("\n")
  return `mailto:${HB_AP_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function line(overrides: Partial<DeliveryLine> = {}): DeliveryLine {
    return {
      id: "l1",
      item_number: "24263",
      brand_sku: null,
      description: "Electrolyte Tablets",
      quantity_ordered: 5616,
      quantity_received: 5000,
      short_units: 616,
      unit_cost: 0.83,
      received_date: "2026-08-22",
      brand_response: null,
      brand_response_note: null,
      ...overrides,
    }
  }

  function order(overrides: Partial<DeliveryOrder> = {}): DeliveryOrder {
    return {
      po_number: "1049300",
      expected_date: "2026-08-24",
      last_received: "2026-08-22",
      is_open: false,
      days_late: null,
      ordered_units: 5616,
      received_units: 5616,
      short_units: 0,
      ordered_value: 4661.28,
      shorted_value: 0,
      late_damages_cap: null,
      invoice: null,
      lines: [line({ quantity_received: 5616, short_units: 0 })],
      ...overrides,
    }
  }

  describe("delivery_status", () => {
    it("names what happened", () => {
      expect(delivery_status(order())).toEqual({ label: "On time, in full", tone: "ok" })
      expect(delivery_status(order({ days_late: 1 }))).toEqual({ label: "1 day late", tone: "late" })
      expect(delivery_status(order({ short_units: 616 }))).toEqual({ label: "616 short", tone: "short" })
      expect(delivery_status(order({ days_late: 3, short_units: 1537 }))).toEqual({ label: "3 days late, 1,537 short", tone: "late_short" })
      expect(delivery_status(order({ is_open: true, days_late: null }))).toEqual({ label: "Being received", tone: "open" })
    })
  })

  describe("filter_orders", () => {
    it("attention is late, short or paid short, and never an open PO", () => {
      const rows = [
        order({ po_number: "ok" }),
        order({ po_number: "late", days_late: 3 }),
        order({ po_number: "short", short_units: 10 }),
        order({ po_number: "open", is_open: true }),
        order({
          po_number: "paid-short",
          invoice: {
            deduction_id: "d",
            invoice_number: "INV-1",
            invoice_date: "2026-08-25",
            amount: 4980,
            check_number: null,
            check_date: null,
            paid_for_received_only: true,
          },
        }),
      ]
      expect(filter_orders(rows, "attention").map((o) => o.po_number)).toEqual(["late", "short", "paid-short"])
      expect(filter_orders(rows, "open").map((o) => o.po_number)).toEqual(["open"])
      expect(filter_orders(rows, "all")).toHaveLength(5)
    })
  })

  describe("shortage_dispute_mailto", () => {
    it("writes to H&B Accounts Payable with the PO, SKU and counts", () => {
      const link = shortage_dispute_mailto(order({ short_units: 616 }), line(), "Alex Smith", "HUK11080")
      expect(link.startsWith(`mailto:${HB_AP_EMAIL}?subject=`)).toBe(true)
      const text = decodeURIComponent(link)
      expect(text).toContain("Receiving discrepancy - PO 1049300 - SKU 24263 - HUK11080")
      expect(text).toContain("received on 22 Aug 2026 as 5,000 of 5,616 units")
      expect(text).toContain("Supplier number: HUK11080")
      expect(text).not.toContain("$")
    })
  })
}
